'use client';

import type { PostArticleProps } from '@routeroyal/theme-core';
import { MarkdownRenderer } from '@routeroyal/ui-core';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { AllysPostCard } from './AllysPostCard';

/**
 * AllysPostArticle - Allys-themed full post display
 * Medium-inspired layout with Allys navy/gold colors
 */
export function AllysPostArticle({
  post,
  relatedPosts = [],
  showShareButtons = true,
  showAuthorBio = true,
  showRelatedPosts = true,
  onShare,
  locale = 'en',
  basePath = '/blog',
  className = '',
  messages,
}: PostArticleProps) {
  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle share
  const handleShare = (platform: 'twitter' | 'facebook' | 'linkedin' | 'copy') => {
    if (onShare) {
      onShare(platform);
      return;
    }

    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = post.title;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        break;
    }
  };

  return (
    <article className={`max-w-4xl mx-auto [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        {/* Category */}
        {post.category && (
          <div className="mb-4">
            <span className="inline-block px-4 py-1.5 text-sm font-semibold text-white bg-[#243462] rounded-full">
              {post.category.name}
            </span>
          </div>
        )}

        {/* Title */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-[#243462]">
          {post.title}
        </h1>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
            {post.excerpt}
          </p>
        )}

        {/* Meta Bar */}
        <div className="flex flex-wrap items-center gap-4 text-gray-600 border-t-2 border-b-2 border-gray-200 py-5">
          {/* Author */}
          <div className="flex items-center gap-4">
            {post.author.avatar ? (
              <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-[#BBA339]/20">
                <Image
                  src={post.author.avatar}
                  alt={post.author.name}
                  fill
                  className="object-cover object-top"
                />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#243462]/10 flex items-center justify-center">
                <svg className="w-7 h-7 text-[#243462]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <div>
              <div className="font-bold text-[#243462] text-lg">{post.author.name}</div>
              {post.author.role && (
                <div className="text-sm text-gray-500">{post.author.role}</div>
              )}
            </div>
          </div>

          <span className="hidden sm:block w-px h-10 bg-gray-300" />

          {/* Date */}
          {post.publishedAt && (
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#BBA339]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <time dateTime={post.publishedAt} className="font-medium">
                {formatDate(post.publishedAt)}
              </time>
            </div>
          )}

          {/* Reading time */}
          {post.readingTime && (
            <>
              <span className="hidden sm:block w-px h-10 bg-gray-300" />
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#BBA339]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{post.readingTime} {messages?.minuteRead || 'min read'}</span>
              </div>
            </>
          )}

          {/* Share buttons */}
          {showShareButtons && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500 hidden md:inline font-medium">
                {messages?.share || 'Share'}:
              </span>
              {[
                { platform: 'twitter' as const, icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
                { platform: 'facebook' as const, icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                { platform: 'linkedin' as const, icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
              ].map(({ platform, icon }) => (
                <button
                  key={platform}
                  onClick={() => handleShare(platform)}
                  className="p-2.5 rounded-full bg-gray-100 hover:bg-[#243462] text-gray-600 hover:text-white transition-all duration-300"
                  aria-label={`Share on ${platform}`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d={icon} />
                  </svg>
                </button>
              ))}
              <button
                onClick={() => handleShare('copy')}
                className="p-2.5 rounded-full bg-gray-100 hover:bg-[#BBA339] text-gray-600 hover:text-white transition-all duration-300"
                aria-label="Copy link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </motion.header>

      {/* Featured Image */}
      {post.featuredImage && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative w-full aspect-[16/9] md:aspect-[2/1] mb-12 rounded-2xl overflow-hidden ring-1 ring-gray-200"
        >
          <Image
            src={post.featuredImage.url}
            alt={post.featuredImage.alt || post.title}
            fill
            className="object-cover"
            priority
          />
          {post.featuredImage.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-sm p-4 pt-8">
              {post.featuredImage.caption}
            </div>
          )}
        </motion.div>
      )}

      {/* Article Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mb-12"
      >
        <MarkdownRenderer
          content={post.content}
          contentFormat={post.contentFormat}
          theme="allys"
        />
      </motion.div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-wrap gap-2 mt-10 pt-8 border-t-2 border-gray-200"
        >
          {post.tags.map((tag) => (
            <span
              key={tag.id}
              className="
                px-4 py-2 text-sm font-medium
                bg-[#BBA339]/10 text-[#243462]
                rounded-full
                hover:bg-[#BBA339]/20
                transition-colors duration-300
                cursor-pointer
              "
            >
              #{tag.name}
            </span>
          ))}
        </motion.div>
      )}

      {/* Author Bio */}
      {showAuthorBio && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200"
        >
          <h3 className="text-sm font-bold text-[#BBA339] uppercase tracking-wide mb-4">
            {messages?.aboutAuthor || 'About the Author'}
          </h3>
          <div className="flex gap-5">
            {post.author.avatar ? (
              <div className="relative w-20 h-20 rounded-full overflow-hidden ring-4 ring-[#BBA339]/20 flex-shrink-0">
                <Image
                  src={post.author.avatar}
                  alt={post.author.name}
                  fill
                  className="object-cover object-top"
                />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#243462]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-10 h-10 text-[#243462]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <div>
              <div className="text-xl font-bold text-[#243462] mb-1">{post.author.name}</div>
              {post.author.role && (
                <div className="text-sm text-[#BBA339] font-medium mb-3">{post.author.role}</div>
              )}
              {post.author.bio && (
                <p className="text-gray-600 leading-relaxed">{post.author.bio}</p>
              )}
              {post.author.socialLinks && (
                <div className="flex items-center gap-3 mt-4">
                  {post.author.socialLinks.twitter && (
                    <a
                      href={post.author.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 hover:bg-[#243462] text-gray-500 hover:text-white transition-all duration-300"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>
                  )}
                  {post.author.socialLinks.linkedin && (
                    <a
                      href={post.author.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 hover:bg-[#243462] text-gray-500 hover:text-white transition-all duration-300"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Related Posts */}
      {showRelatedPosts && relatedPosts.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-16 pt-12 border-t-2 border-gray-200"
        >
          <h2 className="text-3xl font-bold mb-8 text-[#243462]">
            {messages?.relatedPosts || 'Related Posts'}
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((relatedPost) => (
              <AllysPostCard
                key={relatedPost.id}
                post={relatedPost}
                variant="compact"
                showExcerpt={false}
                showCategory
                showAuthor={false}
                locale={locale}
                basePath={basePath}
                {...(messages && { messages })}
              />
            ))}
          </div>
        </motion.section>
      )}
    </article>
  );
}
